import { Injectable, inject, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { GalleryState, Image } from '../interfaces';
import { Storage, ref } from '@angular/fire/storage';
import {
  BUCKET_BASE_PATH,
  GALLERY_IMAGE_HEIGHT,
  GRID_IMAGE_HEIGHT,
  IMG_SRC_URL,
  MAX_ITEMS_PER_PAGE,
  REGISTRY_PATH_TOKEN,
} from '../app.const';
import { HttpClient } from '@angular/common/http';
import { switchMap, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';
import { getDownloadURL } from 'rxfire/storage';

@Injectable()
export class ImageStateService {
  public galleryState = signal<GalleryState>({
    image: undefined,
    loading: true,
    zoomed: false,
  });

  private readonly imageStorage = inject(Storage);
  private readonly httpService = inject(HttpClient);
  private readonly registryPath = inject<string>(REGISTRY_PATH_TOKEN);
  private readonly router = inject(Router);
  private images: Image[] = [];
  private page = 1;
  private maxItemsPerPage = MAX_ITEMS_PER_PAGE;

  public updateGalleryState(galleryState: GalleryState): void {
    this.galleryState.set(galleryState);
  }

  public setGalleryImage(image: Image, index: number): void {
    const url = image.url.replace(`${GRID_IMAGE_HEIGHT}`, `${GALLERY_IMAGE_HEIGHT}`);
    this.updateGalleryState({
      image: {
        ...image,
        index,
        url,
      },
      loading: true,
      zoomed: false,
    });
  }

  public resetGalleryImage(): void {
    this.updateGalleryState({
      image: undefined,
      loading: false,
      zoomed: false,
    });
  }

  public initialiseImages(path: string | undefined): Observable<Image[]> {
    return this.fetchRegistry()
      .pipe(switchMap((images: Image[]) => {
        this.images = images.reverse();
        this.page = 1;

        let galleryImage: Image | undefined;
        let galleryImageIndex = -1;
        let maxIndex = this.maxItemsPerPage;
        if (path) {
          galleryImage = this.images.find((i: Image) => i.url.includes(path));
          galleryImageIndex = this.images.indexOf(galleryImage as Image);
          if (galleryImageIndex !== -1) {
            const targetPage = Math.floor(galleryImageIndex / this.maxItemsPerPage) + 1;
            this.page = targetPage;
            maxIndex = targetPage * this.maxItemsPerPage;
          } else {
            this.router.navigate(['/']);
          }
        }

        const displayImages = this.images.slice(0, maxIndex);
        const mappedImages = environment.useStorage
          ? this.mapImagesToStorage(displayImages)
          : displayImages;
        const mappedGalleryImage = environment.useStorage && galleryImage
          ? this.mapImagesToStorage([galleryImage])[0]
          : galleryImage;

        return of(mappedImages).pipe(
          tap(() => {
            if (mappedGalleryImage) {
              this.setGalleryImage(mappedGalleryImage, galleryImageIndex);
            } else {
              this.resetGalleryImage();
            }
          }),
        );
    }));
  }

  public fetchImages(currentlyDisplayed: number): Observable<Image[]> {
    const start = this.page * this.maxItemsPerPage;
    if (start < this.images.length && currentlyDisplayed < this.images.length) {
      this.page++;
      const displayImages = this.images.slice(start, start + this.maxItemsPerPage);

      return environment.useStorage
        ? of(this.mapImagesToStorage(displayImages))
        : of(displayImages);
    }

    return of([]);
  }

  private fetchRegistry(): Observable<Image[]> {
    return environment.useStorage ?
      getDownloadURL(ref(this.imageStorage, `${BUCKET_BASE_PATH}${this.registryPath}`))
        .pipe(switchMap((url: string) => this.httpService.get<Image[]>(url))) :
      this.httpService.get<Image[]>(this.registryPath);
  }

  private mapImagesToStorage(images: Image[]): Image[] {
    return images.map((image: Image): Image => ({
      ...image,
      url: `${IMG_SRC_URL}${image.url}`,
    }));
  }
}
